import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useFetch from 'use-http';
import { FaCheckCircle, FaSyncAlt } from 'react-icons/fa';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelToast from '../components/PersonnelToast';
import ConfirmCommissionActionDialog from '../dialogs/ConfirmCommissionActionDialog';
import BuildSingleManagerReviewDialog, { IBuildSingleManagerReviewParams } from '../dialogs/BuildSingleManagerReviewDialog';
import CommissionReportParamsDialog, { CommissionReportParamsMode, ICommissionReportParamsSubmit } from '../dialogs/CommissionReportParamsDialog';
import CommissionExcelReportDialog, { ICommissionExcelReportSubmit } from '../dialogs/CommissionExcelReportDialog';
import PcAsmTotalsDialog from '../dialogs/PcAsmTotalsDialog';
import ProductionBonusHistoryDialog from '../dialogs/ProductionBonusHistoryDialog';
import NsContractorsDialog from '../dialogs/NsContractorsDialog';
import PayPeriodRangeDialog from '../dialogs/PayPeriodRangeDialog';
import PmLandmenDialog from '../dialogs/PmLandmenDialog';
import CommissionSearch, { ICommissionSearchCriteria } from '../components/CommissionSearch';
import CommissionSummary from 'src/models/personnel/CommissionSummary';
import CommissionDetailRow from 'src/models/personnel/CommissionDetailRow';
import CommissionPcAsmItem from 'src/models/personnel/CommissionPcAsmItem';
import CommissionPayPeriodSummaryItem from 'src/models/personnel/CommissionPayPeriodSummaryItem';
import CommissionPayPeriodTotalItem from 'src/models/personnel/CommissionPayPeriodTotalItem';
import CommissionPayStatusDetailItem from 'src/models/personnel/CommissionPayStatusDetailItem';
import NonStaffedContractor from 'src/models/personnel/NonStaffedContractor';

const formatCurrency = (value: number) =>
    (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatDate = (value: Date | string) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US');
};

// Mirrors GET_TSD_LANDMAN [oid 134]'s own Highlight_RecordItem('Commission_Detail', v_va,
// 'Comm_Days') - the only Highlight_RecordItem call in the whole form that isn't commented out
// (every other field-level highlight - Days_Worked/Comm_Amt/Comm_Rate/pm_amt/bd_amt/tb_amt/
// pc_amt/pc_tsd_no - is dead code). Green when comm_days > 0 and margin_rate > 0, red otherwise.
const commDaysColor = (row: CommissionDetailRow) => (
    (row.commDays ?? 0) > 0 && (row.marginRate ?? 0) > 0 ? '#8ee88e' : '#f5a3a3'
);

// Mirrors commission.fmt's MAIN canvas - CONTROL toolbar (-> CommissionSearch), the COMMISSION
// (master, "Manager / Crew Chief") and COMMISSION_DETAIL (detail) blocks stacked vertically per
// the real master-detail relation (keyed on comm_id) - selecting a manager row queries that
// manager's own detail breakdown, rather than showing every manager's detail at once.
const CommissionPage = () => {
    const dispatch = useDispatch();
    const { get: getSummary, response: summaryResponse } = useFetch();
    const { get: getDetail, response: detailResponse } = useFetch();
    const { put: putApprove, response: approveResponse } = useFetch();
    const { put: putRebuild, response: rebuildResponse } = useFetch();
    const { put: putBuildManagerReview, response: buildManagerReviewResponse } = useFetch();
    const { put: putBuildSingleManagerReview, response: buildSingleManagerReviewResponse } = useFetch();
    const { get: getSummaryReport, response: summaryReportResponse } = useFetch();
    const { get: getReportsReport, response: reportsReportResponse } = useFetch();
    const { get: getExcelReport, response: excelReportResponse } = useFetch();
    const { get: getPcAsmTotals, response: pcAsmTotalsResponse } = useFetch();
    const [pcAsmItems, setPcAsmItems] = useState<Array<CommissionPcAsmItem>>([]);
    const { get: getPayHistorySummary, response: payHistorySummaryResponse } = useFetch();
    const { get: getPayHistoryTotal, response: payHistoryTotalResponse } = useFetch();
    const { get: getPayHistoryDetail, response: payHistoryDetailResponse } = useFetch();
    const [payHistoryMgrNo, setPayHistoryMgrNo] = useState<number | undefined>(undefined);
    const [payHistorySummaryItems, setPayHistorySummaryItems] = useState<Array<CommissionPayPeriodSummaryItem>>([]);
    const [payHistoryTotalItems, setPayHistoryTotalItems] = useState<Array<CommissionPayPeriodTotalItem>>([]);
    const [payHistoryDetailItems, setPayHistoryDetailItems] = useState<Array<CommissionPayStatusDetailItem>>([]);
    const { get: getNsContractors } = useFetch();
    const [nsContractorItems, setNsContractorItems] = useState<Array<NonStaffedContractor>>([]);
    const { put: putBuildPeriodRange, response: buildPeriodRangeResponse } = useFetch();
    const { get: getMarginsReport, response: marginsReportResponse } = useFetch();
    const { get: getPmLandmen, response: pmLandmenResponse } = useFetch();
    const [pmLandmenName, setPmLandmenName] = useState<string | undefined>(undefined);
    const [pmLandmenItems, setPmLandmenItems] = useState<Array<CommissionDetailRow>>([]);
    // Same "measure my own top offset, then calc(100vh - that)" technique Panel.tsx's own
    // fitPanelToScreen uses internally - but Panel's version only sets a maxHeight cap, which
    // doesn't stretch a short-content panel to fill leftover space, so it can't guarantee the two
    // grids split evenly. This sets a real height instead, letting flex:1 on each grid do the split.
    const gridsContainerRef = useRef<HTMLDivElement>(null);
    const [gridsContainerHeight, setGridsContainerHeight] = useState<string>('auto');
    useEffect(() => {
        if (gridsContainerRef.current) {
            const top = gridsContainerRef.current.getBoundingClientRect().top;
            // The 2px pad isn't for the container itself - it's so the flex:1 split between the two
            // grids has a hair of slack. Splitting an odd-pixel height exactly in half by rounding
            // can leave the second grid's Panel a sub-pixel short, so its bottom border renders right
            // at (and gets shaved by) this container's overflow:hidden edge. A little slack keeps the
            // border fully inside the clip boundary.
            setGridsContainerHeight(`calc(100vh - ${top}px - 1rem - 8px)`);
        }
    }, []);
    const [summary, setSummary] = useState<Array<CommissionSummary>>([]);
    const [detail, setDetail] = useState<Array<CommissionDetailRow>>([]);
    const [selectedCommId, setSelectedCommId] = useState<number | undefined>(undefined);
    const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);
    const [hasQueried, setHasQueried] = useState<boolean>(false);
    const [toast, setToast] = useState<{ message: string; color?: string; persistent?: boolean; } | null>(null);
    // Rebuild needs to re-run the last query afterward (rebuild assigns a new comm_id), so the
    // criteria that produced the current grid has to be remembered.
    const [lastCriteria, setLastCriteria] = useState<ICommissionSearchCriteria | undefined>(undefined);
    // The SINGLE popup only collects a TSDI number (matching the real screen) - the period comes
    // from whatever was in the toolbar's Period No. field when Build Single Manager Review was
    // clicked (oid 43's own :control.period_no read).
    const [singleManagerReviewPeriodNo, setSingleManagerReviewPeriodNo] = useState<number | undefined>(undefined);
    // "Reports", "Summary Report", and "Margins Report" share one dialog
    // (CommissionReportParamsDialog); this just tracks which of the three real screens
    // (EXPENSE_PARAMS vs REPORT_PARAMS vs RANGE_PARMS) it should render as.
    const [reportParamsMode, setReportParamsMode] = useState<CommissionReportParamsMode>('reports');
    // The toolbar's own Period No. at the moment "Reports"/"Summary Report" was clicked - injected
    // into the dialog so the user doesn't have to retype it.
    const [reportParamsPeriodNo, setReportParamsPeriodNo] = useState<number | undefined>(undefined);
    // 'range' mode only - mirrors oid 266's own :range_parms.beg_period/:end_period copy from
    // whatever was already typed into PP_Range's own Beginning/Ending Period fields.
    const [rangeReportBegPeriod, setRangeReportBegPeriod] = useState<number | undefined>(undefined);
    const [rangeReportEndPeriod, setRangeReportEndPeriod] = useState<number | undefined>(undefined);
    const [excelReportPeriodNo, setExcelReportPeriodNo] = useState<number | undefined>(undefined);
    // Tracks which row/action the confirm dialog is currently gating - Approve always confirms,
    // Rebuild only confirms when the row is already approved (see rebuildRow below), Build Manager
    // Review always confirms (it's a whole-period batch operation), and Build Single Manager Review /
    // Add Period only confirm when the server reports that mgr/period is already approved (see
    // submitSingleManagerReview/doAddPeriod below).
    const [pendingAction, setPendingAction] = useState<
        | { type: 'approve' | 'rebuild'; row: CommissionSummary }
        | { type: 'buildManagerReview'; criteria: ICommissionSearchCriteria }
        | { type: 'singleManagerReview'; periodNo: number; mgrNo: number }
        | { type: 'addPeriod'; criteria: ICommissionSearchCriteria }
        | undefined
    >(undefined);
    // Build Manager Review's confirm dialog stays open (spinner + "Building...") for the whole
    // call instead of dismissing immediately, since it's a long-running whole-period batch.
    const [isBuildManagerReviewLoading, setIsBuildManagerReviewLoading] = useState<boolean>(false);

    // Build Single Manager Review's own confirm step is only known AFTER the BuildSingleManager
    // ReviewDialog has already been hidden (the server tells us whether it's needed) - showing the
    // shared confirm dialog from this effect (rather than directly in submitSingleManagerReview)
    // keeps the hideDialog()/showDialog() calls in separate ticks, per the established dialog
    // pattern (never hide+show a dialog in the same tick).
    useEffect(() => {
        if (pendingAction?.type === 'singleManagerReview' || pendingAction?.type === 'addPeriod') {
            dispatch(actions.layout.showDialog(DialogList.CONFIRM_COMMISSION_ACTION));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAction]);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, color, persistent });
    };

    const hideDialog = () => dispatch(actions.layout.hideDialog());

    // Mirrors Access_Non_Staffed_Contractors [oid 1257] - called whenever a build action's result
    // reports HasNonStaffedContractors, instead of just toasting the generic message, so the user
    // sees exactly which manager/period/project/contractor combinations need resolving. A null
    // mgrNo (Build Manager Review's own period-wide check) returns every manager's rows.
    const showNonStaffedContractors = async (periodNo: number, mgrNo?: number) => {
        const params = new URLSearchParams({ periodNo: periodNo.toString() });
        if (mgrNo) {
            params.set('mgrNo', mgrNo.toString());
        }
        const result = await getNsContractors(`/v1.0/Commission/non-staffed-contractors?${params.toString()}`);
        setNsContractorItems(Array.isArray(result) ? result : []);
        dispatch(actions.layout.showDialog(DialogList.NS_CONTRACTORS));
    };

    const runQuery = async (criteria: ICommissionSearchCriteria) => {
        setSummaryLoading(true);
        setToast(null);
        setSelectedCommId(undefined);
        setDetail([]);
        setLastCriteria(criteria);

        const params = new URLSearchParams({ periodNo: criteria.periodNo.toString(), commissionType: criteria.commissionType });
        if (criteria.mgrNo) {
            params.set('mgrNo', criteria.mgrNo.toString());
        }
        if (criteria.clNo) {
            params.set('clNo', criteria.clNo.toString());
        }
        if (criteria.prNo) {
            params.set('prNo', criteria.prNo.toString());
        }
        if (criteria.tsdNo) {
            params.set('tsdNo', criteria.tsdNo.toString());
        }

        const result = await getSummary(`/v1.0/Commission/summary?${params.toString()}`);
        setSummary(summaryResponse.ok && Array.isArray(result) ? result : []);
        setHasQueried(true);
        setSummaryLoading(false);
    };

    const selectRow = async (row: CommissionSummary) => {
        setSelectedCommId(row.commId);
        setDetailLoading(true);
        const result = await getDetail(`/v1.0/Commission/${row.commId}/detail`);
        setDetail(detailResponse.ok && Array.isArray(result) ? result : []);
        setDetailLoading(false);
    };

    // "PM Landmen" drill-down (new feature, not an Oracle port) - double-clicking a Commission
    // Detail row looks that row's own tsd_no up as a manager in its own right, showing their own
    // Commission Detail (the actual landmen under them) if they have one - e.g. a PM whose
    // production is what a VP-scoped Area Manager's own row is crediting. Reuses the period/
    // commissionType from whatever search produced the currently-loaded grid. A tsd_no with no
    // manager-level row of their own (a plain landman) has nothing to drill into - double-click is
    // silently a no-op rather than opening an empty dialog.
    const openPmLandmen = async (row: CommissionDetailRow) => {
        if (!lastCriteria || !row.tsdNo) {
            return;
        }
        const params = new URLSearchParams({ periodNo: lastCriteria.periodNo.toString(), commissionType: lastCriteria.commissionType, mgrNo: row.tsdNo.toString() });
        const result = await getPmLandmen(`/v1.0/Commission/detail-by-manager?${params.toString()}`);
        if (!pmLandmenResponse.ok || !Array.isArray(result) || result.length === 0) {
            return;
        }
        setPmLandmenName(row.name);
        setPmLandmenItems(result);
        dispatch(actions.layout.showDialog(DialogList.PM_LANDMEN));
    };

    // Mirrors the "Excel Report" toolbar button's common-case path (oid 565, Go_Block('Excel')) -
    // the other window this button can route to, "Excel_AMPM" (different fields, reached only when
    // mod(period_no, 100) is 4/10/16/22), is not covered yet.
    const openExcelReport = (criteria: ICommissionSearchCriteria) => {
        setExcelReportPeriodNo(criteria.periodNo);
        dispatch(actions.layout.showDialog(DialogList.COMMISSION_EXCEL_REPORT));
    };

    // Mirrors the Excel window's OK button (oid 565) -> XL_PM_Margin -> targets the already-deployed
    // PMMarginReport on the report server.
    const submitExcelReport = async (params: ICommissionExcelReportSubmit) => {
        const query = new URLSearchParams({ begPeriod: String(params.begPeriod), endPeriod: String(params.endPeriod) });
        if (params.mgrNo) {
            query.set('mgrNo', String(params.mgrNo));
        }

        await getExcelReport(`/v1.0/reports/GetReport/PMMarginReport?${query.toString()}`);

        if (!excelReportResponse.ok) {
            showToast('Unable to generate the report. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }

        if (excelReportResponse.status === 204) {
            showToast('No commission records matched those filters.', 'rgba(255,0,0,0.5)');
            return;
        }

        const blob = await excelReportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'PMMarginReport.xlsx';
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        hideDialog();
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 42] - "PC/ASM Totals". Available in both modes; requires a
    // manager to already be selected/entered (Oracle's own :control.tsd_no is always populated by
    // this point in the real form - our port has no such guarantee since the field can be cleared).
    const openPcAsmTotals = async (criteria: ICommissionSearchCriteria) => {
        setToast(null);
        if (!criteria.mgrNo) {
            showToast('Please select a manager first.', 'rgba(255,0,0,0.5)');
            return;
        }

        const params = new URLSearchParams({ periodNo: criteria.periodNo.toString(), mgrNo: criteria.mgrNo.toString() });
        const result = await getPcAsmTotals(`/v1.0/Commission/pcasm-summary?${params.toString()}`);

        if (!pcAsmTotalsResponse.ok) {
            showToast('Unable to fetch PC/ASM totals. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }

        setPcAsmItems(Array.isArray(result) ? result : []);
        dispatch(actions.layout.showDialog(DialogList.PCASM_TOTALS));
    };

    // Mirrors button B_PB_HISTORY -> "Production Bonus History" (canvas HIST_CONTROL) - opens
    // straight to the top Summary grid for whichever manager is currently entered, same
    // "requires a manager first" guard as PC/ASM Totals above.
    const openBonusHistory = async (criteria: ICommissionSearchCriteria) => {
        setToast(null);
        if (!criteria.mgrNo) {
            showToast('Please select a manager first.', 'rgba(255,0,0,0.5)');
            return;
        }

        setPayHistoryMgrNo(criteria.mgrNo);
        setPayHistoryTotalItems([]);
        setPayHistoryDetailItems([]);

        const params = new URLSearchParams({ mgrNo: criteria.mgrNo.toString() });
        const result = await getPayHistorySummary(`/v1.0/Commission/pay-history/summary?${params.toString()}`);

        if (!payHistorySummaryResponse.ok) {
            showToast('Unable to fetch bonus history. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }

        setPayHistorySummaryItems(Array.isArray(result) ? result : []);
        dispatch(actions.layout.showDialog(DialogList.PRODUCTION_BONUS_HISTORY));
    };

    // Mirrors the Summary grid's row-select trigger driving its own detail relation (the Total
    // grid, by payroll_period).
    const selectPayHistoryPayrollPeriod = async (payrollPeriod: number) => {
        setPayHistoryDetailItems([]);
        if (!payHistoryMgrNo) {
            return;
        }
        const params = new URLSearchParams({ mgrNo: payHistoryMgrNo.toString(), payrollPeriod: payrollPeriod.toString() });
        const result = await getPayHistoryTotal(`/v1.0/Commission/pay-history/total?${params.toString()}`);
        setPayHistoryTotalItems(payHistoryTotalResponse.ok && Array.isArray(result) ? result : []);
    };

    // Mirrors the Total grid's row-select trigger driving its own detail relation (the flat
    // Detail grid, by payroll_period + period_no).
    const selectPayHistoryPeriodNo = async (payrollPeriod: number, periodNo: number) => {
        if (!payHistoryMgrNo) {
            return;
        }
        const params = new URLSearchParams({ mgrNo: payHistoryMgrNo.toString(), payrollPeriod: payrollPeriod.toString(), periodNo: periodNo.toString() });
        const result = await getPayHistoryDetail(`/v1.0/Commission/pay-history/detail?${params.toString()}`);
        setPayHistoryDetailItems(payHistoryDetailResponse.ok && Array.isArray(result) ? result : []);
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 46] -> Go_Block('PP_Range') - "Multiple Periods", admin-only
    // (see CommissionSearch's own canSearchAnyManager gate).
    const openMultiplePeriods = () => {
        setToast(null);
        dispatch(actions.layout.showDialog(DialogList.PAY_PERIOD_RANGE));
    };

    // Mirrors PP_Range's own "Build" button (oid 263) - loops every real period_no in the range
    // server-side (see CommissionService.BuildPeriodRange). Oracle's own version live-updates
    // :pp_range.msg per period as it goes; this port has no equivalent mid-call UI refresh, so it
    // resolves once with a joined summary of every period's own result instead. Returns a non-empty
    // string only for genuine errors (PayPeriodRangeDialog shows those inline in red) - a
    // successful build is reported via the page's own toast instead, matching every other build
    // action on this screen.
    const doBuildPeriodRange = async (begPeriod: number, endPeriod: number): Promise<string> => {
        const params = new URLSearchParams({ begPeriod: begPeriod.toString(), endPeriod: endPeriod.toString() });
        const result = await putBuildPeriodRange(`/v1.0/Commission/build-period-range?${params.toString()}`);

        if (!buildPeriodRangeResponse.ok) {
            return 'Unable to build. Please try again.';
        }

        if (!Array.isArray(result) || result.length === 0) {
            return `No pay periods found between ${begPeriod} and ${endPeriod}.`;
        }

        const allSucceeded = result.every((r: { success: boolean }) => r.success);
        const message = result.map((r: { message: string }) => r.message).join(' ');
        showToast(message, allSucceeded ? undefined : 'rgba(255,0,0,0.5)');
        return '';
    };

    // Mirrors PP_Range's own "Reports" button (oid 266) -> Go_Block('Range_Parms') - carries the
    // already-entered Beginning/Ending Period across, matching oid 266's own
    // :range_parms.beg_period := :pp_range.beg_period copy.
    const openMarginsReport = (begPeriod: number, endPeriod: number) => {
        setReportParamsMode('range');
        setRangeReportBegPeriod(begPeriod);
        setRangeReportEndPeriod(endPeriod);
        dispatch(actions.layout.showDialog(DialogList.COMMISSION_REPORT_PARAMS));
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 49] -> Go_Block('Expense_Params') - "Reports" and "Summary
    // Report" share one dialog component; this just picks which real screen's fields it shows.
    const openReports = (criteria: ICommissionSearchCriteria) => {
        setReportParamsMode('reports');
        setReportParamsPeriodNo(criteria.periodNo);
        dispatch(actions.layout.showDialog(DialogList.COMMISSION_REPORT_PARAMS));
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 52] -> Go_Block('Report_Params').
    const openSummaryReport = (criteria: ICommissionSearchCriteria) => {
        setReportParamsMode('summary');
        setReportParamsPeriodNo(criteria.periodNo);
        dispatch(actions.layout.showDialog(DialogList.COMMISSION_REPORT_PARAMS));
    };

    const submitReportParams = (params: ICommissionReportParamsSubmit) => {
        if (params.mode === 'reports') {
            submitReports(params);
            return;
        }
        if (params.mode === 'range') {
            submitMarginsReport(params);
            return;
        }
        submitSummaryReport(params);
    };

    // Mirrors Expense_Params's OK button [oid 182] -> COMMRPT_V4.RDF (Report By='M', ported as
    // CommissionReportByManager.rdl) / COMMPROS_V4.RDF (Report By='P', ported as
    // CommissionReportByProspect.rdl). Same 204-means-nothing-matched blob-download pattern as
    // Summary Report/Hist. Perf. Bonus.
    const submitReports = async (params: ICommissionReportParamsSubmit) => {
        if (!params.periodNo) {
            showToast('Please enter a pay period.', 'rgba(255,0,0,0.5)');
            return;
        }

        const query = new URLSearchParams({ periodNo: String(params.periodNo), includeDayRate: String(!!params.includeDayRate) });
        if (params.mgrNo) {
            query.set('mgrNo', String(params.mgrNo));
        }
        if (params.clNo) {
            query.set('clNo', String(params.clNo));
        }

        const reportName = params.reportBy === 'M' ? 'CommissionReportByManager' : 'CommissionReportByProspect';
        await getReportsReport(`/v1.0/reports/GetReport/${reportName}?${query.toString()}`);

        if (!reportsReportResponse.ok) {
            showToast('Unable to generate the report. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }

        if (reportsReportResponse.status === 204) {
            showToast('No commission records matched those filters.', 'rgba(255,0,0,0.5)');
            return;
        }

        const blob = await reportsReportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${reportName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        hideDialog();
    };

    // Mirrors Report_Params's OK button [oid 340] -> COMMRPT_SUMMARY_V4.RDF, ported as
    // CommissionSummaryReport.rdl. Same 204-means-nothing-matched blob-download pattern as
    // EmployeePayrollPage's Hist. Perf. Bonus report.
    const submitSummaryReport = async (params: ICommissionReportParamsSubmit) => {
        if (!params.begPeriod || !params.endPeriod) {
            showToast('Please enter a beginning and ending pay period.', 'rgba(255,0,0,0.5)');
            return;
        }

        const query = new URLSearchParams({ begPeriod: String(params.begPeriod), endPeriod: String(params.endPeriod), includeDayRate: String(!!params.includeDayRate) });
        if (params.mgrNo) {
            query.set('mgrNo', String(params.mgrNo));
        }
        if (params.clNo) {
            query.set('clNo', String(params.clNo));
        }

        await getSummaryReport(`/v1.0/reports/GetReport/CommissionSummaryReport?${query.toString()}`);

        if (!summaryReportResponse.ok) {
            showToast('Unable to generate the report. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }

        if (summaryReportResponse.status === 204) {
            showToast('No commission records matched those filters.', 'rgba(255,0,0,0.5)');
            return;
        }

        const blob = await summaryReportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'CommissionSummaryReport.pdf';
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        hideDialog();
    };

    // Mirrors RANGE_PARMS's OK button [oid 288] -> COMMRPT_V2.RDF (Report By='M', ported as
    // MarginsByManager) / COMMPROS_V2.RDF (Report By='P', ported as MarginsByProject). Same
    // 204-means-nothing-matched blob-download pattern as the other report buttons on this page.
    const submitMarginsReport = async (params: ICommissionReportParamsSubmit) => {
        if (!params.begPeriod || !params.endPeriod) {
            showToast('Please enter a beginning and ending period.', 'rgba(255,0,0,0.5)');
            return;
        }

        const query = new URLSearchParams({ begPeriod: String(params.begPeriod), endPeriod: String(params.endPeriod), includeDayRate: String(!!params.includeDayRate) });
        if (params.clNo) {
            query.set('clNo', String(params.clNo));
        }

        const reportName = params.reportBy === 'M' ? 'MarginsByManager' : 'MarginsByProject';
        await getMarginsReport(`/v1.0/reports/GetReport/${reportName}?${query.toString()}`);

        if (!marginsReportResponse.ok) {
            showToast('Unable to generate the report. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }

        if (marginsReportResponse.status === 204) {
            showToast('No commission records matched those filters.', 'rgba(255,0,0,0.5)');
            return;
        }

        const blob = await marginsReportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${reportName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        hideDialog();
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 40] -> Build_Mgr_Commissions - builds every not-yet-approved
    // manager for the period at once. This is a real whole-period batch call (can take a while), so
    // it's gated by a confirm dialog that stays open with a spinner until the call returns.
    // Returns true when it has already navigated to a different dialog (Non-Staffed Contractors),
    // so confirmPendingAction knows not to hideDialog() out from under it afterward.
    const doBuildManagerReview = async (criteria: ICommissionSearchCriteria): Promise<boolean> => {
        setToast(null);
        const params = new URLSearchParams({ periodNo: criteria.periodNo.toString() });
        const result = await putBuildManagerReview(`/v1.0/Commission/build-manager-review?${params.toString()}`);

        if (!buildManagerReviewResponse.ok) {
            showToast('Unable to build. Please try again.', 'rgba(255,0,0,0.5)');
            return false;
        }

        if (result?.hasNonStaffedContractors) {
            await showNonStaffedContractors(criteria.periodNo);
            return true;
        }

        if (result?.success) {
            await runQuery(criteria);
        }
        showToast(result?.message ?? '', result?.success ? undefined : 'rgba(255,0,0,0.5)');
        return false;
    };

    const buildManagerReview = (criteria: ICommissionSearchCriteria) => {
        setPendingAction({ type: 'buildManagerReview', criteria });
        dispatch(actions.layout.showDialog(DialogList.CONFIRM_COMMISSION_ACTION));
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 43] - opens the SINGLE popup (Enter Manager/Crew Chief No.).
    const openBuildSingleManagerReview = (criteria: ICommissionSearchCriteria) => {
        setSingleManagerReviewPeriodNo(criteria.periodNo);
        dispatch(actions.layout.showDialog(DialogList.BUILD_SINGLE_MANAGER_REVIEW));
    };

    // Mirrors manager_commission.fmt's own ADD_PERIOD trigger (oid 34) - the manager-scope
    // equivalent of per-row Rebuild (CommissionSearch only shows this button, in place of Build
    // Manager Review/Build Single Manager Review, when the logged-in user isn't Admin/Accounting
    // Admin - see CommissionSearch's canSearchAnyManager). criteria.mgrNo is already forced to the
    // user's own tsdNo by that same read-only Manager field, so this always builds just their own
    // commission, never anyone else's. Reuses the exact /rebuild endpoint per-row Rebuild uses -
    // ADD_PERIOD's current-era branch calls the identical PM_Comm.Build_PM_Commission_BD('S'/'B') +
    // Commission_Mark_Paid('PM') sequence RebuildCommission already implements; ADD_PERIOD's
    // Period_Paid guard is skipped here for the same reason RebuildCommission skips it (Oracle's
    // own Period_Paid has been a hardcoded no-op since 2023-06-13).
    // confirmed=false on the first call (mirrors manager_commission.fmt's own oid 108 "Rebuild"
    // trigger, which this shares its /rebuild endpoint with) - if the server reports the period's
    // already approved, addPeriod below shows the same confirm dialog and re-calls with
    // confirmed=true, matching submitSingleManagerReview's own pattern just below.
    const doAddPeriod = async (criteria: ICommissionSearchCriteria, confirmed: boolean) => {
        setToast(null);
        if (!criteria.mgrNo) {
            showToast('Unable to determine your manager number.', 'rgba(255,0,0,0.5)');
            return;
        }
        const params = new URLSearchParams({ periodNo: criteria.periodNo.toString(), mgrNo: criteria.mgrNo.toString(), confirmed: confirmed.toString() });
        const result = await putRebuild(`/v1.0/Commission/rebuild?${params.toString()}`);

        if (!rebuildResponse.ok) {
            showToast('Unable to build. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }

        if (result?.requiresConfirmation) {
            setPendingAction({ type: 'addPeriod', criteria });
            return;
        }

        if (result?.hasNonStaffedContractors) {
            await showNonStaffedContractors(criteria.periodNo, criteria.mgrNo);
            return;
        }

        if (result?.success) {
            await runQuery(criteria);
        }
        showToast(result?.message ?? '', result?.success ? undefined : 'rgba(255,0,0,0.5)');
    };

    const addPeriod = (criteria: ICommissionSearchCriteria) => {
        doAddPeriod(criteria, false);
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 297] (the SINGLE block's own OK button). confirmed=false on
    // the first call; if the server reports RequiresConfirmation (mgr/period already approved),
    // this hides the entry dialog and defers to the shared confirm dialog via pendingAction/effect
    // above, rather than showing it directly here.
    const submitSingleManagerReview = async (periodNo: number, mgrNo: number, confirmed: boolean) => {
        setToast(null);
        const params = new URLSearchParams({ periodNo: periodNo.toString(), mgrNo: mgrNo.toString(), confirmed: confirmed.toString() });
        const result = await putBuildSingleManagerReview(`/v1.0/Commission/build-single-manager-review?${params.toString()}`);

        if (!buildSingleManagerReviewResponse.ok) {
            showToast('Unable to build. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }

        if (result?.requiresConfirmation) {
            setPendingAction({ type: 'singleManagerReview', periodNo, mgrNo });
            return;
        }

        if (result?.success && lastCriteria) {
            await runQuery(lastCriteria);
        }
        showToast(`${mgrNo}: ${result?.message ?? ''}`, result?.success ? undefined : 'rgba(255,0,0,0.5)');
    };

    const handleBuildSingleManagerReviewSubmit = (params: IBuildSingleManagerReviewParams) => {
        hideDialog();
        if (singleManagerReviewPeriodNo) {
            submitSingleManagerReview(singleManagerReviewPeriodNo, params.mgrNo, false);
        }
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 116] - stamps approve_date/approve_by on this manager's row.
    // No already-approved re-check here - the button is disabled once approveDate is set, so this
    // can only be reached from a not-yet-approved row.
    const doApprove = async (row: CommissionSummary) => {
        setToast(null);
        const result = await putApprove(`/v1.0/Commission/${row.commId}/approve`);
        if (!approveResponse.ok) {
            showToast('Unable to approve. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }
        setSummary((prev) => prev.map((r) => (
            r.commId === row.commId ? { ...r, approveDate: new Date(), approveBy: result?.approveBy } : r
        )));
        showToast(`${row.mgrNo} - ${row.name}'s commission has been approved.`);
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 119] (current-era branch) - everything past the confirm is
    // server-side. Rebuilding assigns a new comm_id, so the grid is re-queried afterward rather
    // than patched.
    const doRebuild = async (row: CommissionSummary) => {
        setToast(null);
        const params = new URLSearchParams({ periodNo: row.periodNo.toString(), mgrNo: row.mgrNo.toString() });
        const result = await putRebuild(`/v1.0/Commission/rebuild?${params.toString()}`);

        if (!rebuildResponse.ok) {
            showToast('Unable to rebuild. Please try again.', 'rgba(255,0,0,0.5)');
            return;
        }

        if (result?.hasNonStaffedContractors) {
            await showNonStaffedContractors(row.periodNo, row.mgrNo);
            return;
        }

        if (result?.success && lastCriteria) {
            await runQuery(lastCriteria);
        }
        // The backend's own message text is generic ("Commission for this period have been
        // rebuilt...", matching Oracle's :control.msg) - prefix with who, since this only ever
        // rebuilds one manager at a time, not the whole period.
        const message = result?.success
            ? `${row.mgrNo} - ${row.name}: ${result?.message ?? ''}`
            : (result?.message ?? '');
        showToast(message, result?.success ? undefined : 'rgba(255,0,0,0.5)');
    };

    // Every Approve click confirms - there's no other gating state to key off (the button is only
    // ever reachable from a not-yet-approved row in the first place).
    const approveRow = (row: CommissionSummary) => {
        setPendingAction({ type: 'approve', row });
        dispatch(actions.layout.showDialog(DialogList.CONFIRM_COMMISSION_ACTION));
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 119]'s own Alert_Message Yes/No - only confirms when the
    // row is already approved; otherwise rebuilds immediately, same as Oracle.
    const rebuildRow = (row: CommissionSummary) => {
        if (row.approveDate) {
            setPendingAction({ type: 'rebuild', row });
            dispatch(actions.layout.showDialog(DialogList.CONFIRM_COMMISSION_ACTION));
            return;
        }
        doRebuild(row);
    };

    const confirmPendingAction = async () => {
        if (pendingAction?.type === 'approve') {
            hideDialog();
            doApprove(pendingAction.row);
            setPendingAction(undefined);
        } else if (pendingAction?.type === 'rebuild') {
            hideDialog();
            doRebuild(pendingAction.row);
            setPendingAction(undefined);
        } else if (pendingAction?.type === 'buildManagerReview') {
            // Deliberately doesn't hideDialog()/clear pendingAction yet - the dialog persists with
            // a spinner (via isBuildManagerReviewLoading) until the whole-period call returns. If
            // that call already navigated to the Non-Staffed Contractors dialog, skip hideDialog()
            // here - calling it unconditionally would immediately clobber the dialog it just opened.
            setIsBuildManagerReviewLoading(true);
            const openedAnotherDialog = await doBuildManagerReview(pendingAction.criteria);
            setIsBuildManagerReviewLoading(false);
            if (!openedAnotherDialog) {
                hideDialog();
            }
            setPendingAction(undefined);
        } else if (pendingAction?.type === 'singleManagerReview') {
            hideDialog();
            submitSingleManagerReview(pendingAction.periodNo, pendingAction.mgrNo, true);
            setPendingAction(undefined);
        } else if (pendingAction?.type === 'addPeriod') {
            hideDialog();
            doAddPeriod(pendingAction.criteria, true);
            setPendingAction(undefined);
        }
    };

    return (
        <div>
            <PersonnelNavBar />
            {toast && (
                <PersonnelToast
                    message={toast.message}
                    color={toast.color}
                    onClose={() => setToast(null)}
                    persistent={toast.persistent}
                />
            )}
            <CommissionSearch
                onSearch={runQuery}
                onBuildManagerReview={buildManagerReview}
                onBuildSingleManagerReview={openBuildSingleManagerReview}
                onAddPeriod={addPeriod}
                onReports={openReports}
                onSummaryReport={openSummaryReport}
                onExcelReport={openExcelReport}
                onPcAsmTotals={openPcAsmTotals}
                onBonusHistory={openBonusHistory}
                onMultiplePeriods={openMultiplePeriods}
            />
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <div ref={gridsContainerRef} style={{ display: 'flex', flexDirection: 'column', height: gridsContainerHeight, overflow: 'hidden' }}>
                            <div style={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <Panel panelTitle="Manager / Crew Chief" height="100%">
                                    <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderSpacing: 0, borderCollapse: 'separate' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Manager / Crew Chief</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Days</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Margin Amount</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Projects</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Landmen</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Approve Date</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Approve By</th>
                                                    <th style={{ whiteSpace: 'nowrap' }} />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {summaryLoading && (
                                                    <tr><td colSpan={8} className="bold-centered"><LoadingSpinner /></td></tr>
                                                )}
                                                {!summaryLoading && summary.map((row) => (
                                                    <tr
                                                        key={row.commId}
                                                        onClick={() => selectRow(row)}
                                                        style={{ textAlign: 'center', cursor: 'pointer', backgroundColor: row.commId === selectedCommId ? '#D0E5F5' : undefined }}
                                                    >
                                                        <td className="arHistoryCell">{row.mgrNo} - {row.name}{row.isAreaManager ? ' (Area Manager)' : ''}</td>
                                                        <td className="arHistoryCell">{row.commDays}</td>
                                                        <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(row.marginAmt)}</td>
                                                        <td className="arHistoryCell">{row.noProspects}</td>
                                                        <td className="arHistoryCell">{row.noLandman}</td>
                                                        <td className="arHistoryCell">{formatDate(row.approveDate)}</td>
                                                        <td className="arHistoryCell">{row.approveBy}</td>
                                                        <td className="arHistoryCell" style={{ whiteSpace: 'nowrap' }}>
                                                            {/* B_APPROVE [oid 116] has no un-approve path - once approve_date is set, Oracle's own
                                                                click handler just pops "This period has already been approved." and does nothing.
                                                                Disabling here instead of reproducing that useless dialog. */}
                                                            <Button
                                                                title={row.approveDate ? 'Approved' : 'Approve'}
                                                                text=""
                                                                className="blue-hover-button"
                                                                style={{ border: 'none', background: 'transparent', padding: '.2rem' }}
                                                                disabled={!!row.approveDate}
                                                                reactIcon={FaCheckCircle}
                                                                iconStyle={{ fill: row.approveDate ? '#bbb' : 'green', width: '1.5rem', height: '1.5rem', verticalAlign: 'middle' }}
                                                                onClick={(e) => { e.stopPropagation(); approveRow(row); }}
                                                            />
                                                            <Button
                                                                title="Rebuild"
                                                                text=""
                                                                className="blue-hover-button"
                                                                style={{ border: 'none', background: 'transparent', padding: '.2rem' }}
                                                                reactIcon={FaSyncAlt}
                                                                iconStyle={{ fill: 'rgb(99, 147, 193)', width: '1.5rem', height: '1.5rem', verticalAlign: 'middle' }}
                                                                onClick={(e) => { e.stopPropagation(); rebuildRow(row); }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                                {!summaryLoading && hasQueried && summary.length === 0 && (
                                                    <tr><td colSpan={8} className="bold-centered">No Data Found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Panel>
                            </div>
                            <div style={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <Panel panelTitle="Commission Detail" height="100%">
                                    <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderSpacing: 0, borderCollapse: 'separate' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Client</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Project</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Subcode</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Employee #</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Landman Name</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Bill Rate</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Day Rate</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Margin Rate</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Days</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Margin Type</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Margin Amount</th>
                                                    <th style={{ whiteSpace: 'nowrap' }}>Invoice #</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detailLoading && (
                                                    <tr><td colSpan={12} className="bold-centered"><LoadingSpinner /></td></tr>
                                                )}
                                                {!detailLoading && detail.map((row, index) => (
                                                    <tr
                                                        key={`${row.commId}-${row.clNo}-${row.prNo}-${row.tsdNo}-${index}`}
                                                        style={{ textAlign: 'center', cursor: 'pointer' }}
                                                        onDoubleClick={() => openPmLandmen(row)}
                                                    >
                                                        <td className="arHistoryCell">{row.clNo}</td>
                                                        <td className="arHistoryCell">{row.prNo}</td>
                                                        <td className="arHistoryCell">{row.scNo}</td>
                                                        <td className="arHistoryCell">{row.tsdNo}</td>
                                                        <td className="arHistoryCell">{row.name}</td>
                                                        <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(row.billRate)}</td>
                                                        <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(row.dayRate)}</td>
                                                        <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(row.marginRate)}</td>
                                                        <td className="arHistoryCell" style={{ backgroundColor: commDaysColor(row) }}>{row.commDays}</td>
                                                        <td className="arHistoryCell">{row.dCommType}</td>
                                                        <td className="arHistoryCell" style={{ textAlign: 'right' }}>{formatCurrency(row.marginAmt)}</td>
                                                        <td className="arHistoryCell">{row.invoiceNo}</td>
                                                    </tr>
                                                ))}
                                                {!detailLoading && selectedCommId && detail.length === 0 && (
                                                    <tr><td colSpan={12} className="bold-centered">No Data Found</td></tr>
                                                )}
                                                {!detailLoading && !selectedCommId && (
                                                    <tr><td colSpan={12} className="bold-centered">Select a manager above to view detail</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Panel>
                            </div>
                        </div>
                    </Panel>
                </Page.Section>
            </Page>
            <ConfirmCommissionActionDialog
                dialogName={DialogList.CONFIRM_COMMISSION_ACTION}
                hideDialog={hideDialog}
                heading={
                    pendingAction?.type === 'approve' ? 'Approve Commission'
                        : pendingAction?.type === 'rebuild' ? 'Rebuild Commission'
                            : pendingAction?.type === 'singleManagerReview' ? 'Rebuild Commission'
                                : pendingAction?.type === 'addPeriod' ? 'Rebuild Commission'
                                    : 'Build Manager Review'
                }
                label={
                    pendingAction?.type === 'approve'
                        ? `Approve commission for ${pendingAction.row.mgrNo} - ${pendingAction.row.name}?`
                        : pendingAction?.type === 'rebuild' || pendingAction?.type === 'singleManagerReview' || pendingAction?.type === 'addPeriod'
                            ? 'The commission for this period has already been approved. Are you sure you want to rebuild it?'
                            : `Build commissions for every not-yet-approved manager in period ${pendingAction?.type === 'buildManagerReview' ? pendingAction.criteria.periodNo : ''}?`
                }
                confirmText={
                    pendingAction?.type === 'approve' ? 'Approve'
                        : pendingAction?.type === 'rebuild' || pendingAction?.type === 'singleManagerReview' || pendingAction?.type === 'addPeriod' ? 'Rebuild'
                            : 'Build'
                }
                loadingText="Building..."
                loading={pendingAction?.type === 'buildManagerReview' && isBuildManagerReviewLoading}
                onConfirm={confirmPendingAction}
            />
            <BuildSingleManagerReviewDialog
                dialogName={DialogList.BUILD_SINGLE_MANAGER_REVIEW}
                hideDialog={hideDialog}
                onSubmit={handleBuildSingleManagerReviewSubmit}
            />
            <CommissionReportParamsDialog
                dialogName={DialogList.COMMISSION_REPORT_PARAMS}
                hideDialog={hideDialog}
                mode={reportParamsMode}
                onSubmit={submitReportParams}
                defaultPeriodNo={reportParamsPeriodNo}
                defaultBegPeriod={rangeReportBegPeriod}
                defaultEndPeriod={rangeReportEndPeriod}
            />
            <CommissionExcelReportDialog
                dialogName={DialogList.COMMISSION_EXCEL_REPORT}
                hideDialog={hideDialog}
                onSubmit={submitExcelReport}
                defaultPeriodNo={excelReportPeriodNo}
            />
            <PcAsmTotalsDialog
                dialogName={DialogList.PCASM_TOTALS}
                items={pcAsmItems}
                onClose={hideDialog}
            />
            <ProductionBonusHistoryDialog
                dialogName={DialogList.PRODUCTION_BONUS_HISTORY}
                mgrNo={payHistoryMgrNo}
                summaryItems={payHistorySummaryItems}
                totalItems={payHistoryTotalItems}
                detailItems={payHistoryDetailItems}
                onSelectPayrollPeriod={selectPayHistoryPayrollPeriod}
                onSelectPeriodNo={selectPayHistoryPeriodNo}
                onClose={hideDialog}
            />
            <NsContractorsDialog
                dialogName={DialogList.NS_CONTRACTORS}
                items={nsContractorItems}
                onClose={hideDialog}
            />
            <PayPeriodRangeDialog
                dialogName={DialogList.PAY_PERIOD_RANGE}
                onBuild={doBuildPeriodRange}
                onReports={openMarginsReport}
                onClose={hideDialog}
            />
            <PmLandmenDialog
                dialogName={DialogList.PM_LANDMEN}
                pmName={pmLandmenName}
                items={pmLandmenItems}
                onClose={hideDialog}
            />
        </div>
    );
};

export default CommissionPage;



// WEBPACK FOOTER //
// ./src/components/personnel/pages/CommissionPage.tsx